import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  MdAdd,
  MdDelete,
  MdCheckCircle,
} from "react-icons/md";
import { FiDatabase, FiFileText } from "react-icons/fi";
import TableNavTabs from "./TableNavTabs";
import DynamicTable from "../../Components/Table/DynamicTable";
import Loader from "../../Components/Loader/Loader";
import {
  fetchAllTables,
  fetchTableDetails,
  addTableWithFields,
  clearStatusMessage,
} from "../../Redux/Slice/TableManagementSlice";

const TableMaster = () => {
  const dispatch = useDispatch();

  const {
    tables,
    tablesLoading,
    selectedTable,
    selectedTableLoading,
    statusMessage,
    actionLoading,
    actionError,
  } = useSelector((state) => state.TableManagement);

  // Form State
  const [formValues, setFormValues] = useState({
    tableName: "",
    fieldName: "",
  });
  const [fieldNames, setFieldNames] = useState([]);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);

  // Table selection
  const [selectedRow, setSelectedRow] = useState(null);

  // Initial load
  useEffect(() => {
    dispatch(fetchAllTables());
  }, [dispatch]);

  // Handle toast notifications
  useEffect(() => {
    if (statusMessage) {
      toast.success(statusMessage);
      dispatch(clearStatusMessage());
    }
    if (actionError) {
      toast.error(actionError);
      dispatch(clearStatusMessage());
    }
  }, [statusMessage, actionError, dispatch]);

  // Main tables rows for DynamicTable
  const tableRows = useMemo(() => {
    const list = tables?.data || [];
    return list.map((item, index) => ({
      id: index + 1,
      tableName: item?.tblName,
      FildTotal: item?.Field?.length || 0,
      Mid: item?._id,
    }));
  }, [tables]);

  // Fields rows for selected table for DynamicTable
  const fieldRows = useMemo(() => {
    const fieldList = selectedTable?.data?.Field || [];
    return fieldList.map((item, index) => ({
      id: index + 1,
      FildName: item?.name || "Unnamed Field",
      Mid: item?._id,
      date: item?.date ? new Date(item.date).toLocaleDateString() : "-",
    }));
  }, [selectedTable]);

  // Row selection handler
  const handleRowClick = (row) => {
    const activeRow = row?.row || row;
    setSelectedRow(activeRow);
    if (activeRow?.Mid) {
      dispatch(fetchTableDetails(activeRow.Mid));
    }
  };

  // Form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add or update field name in list
  const addOrUpdateFieldName = () => {
    const trimmed = formValues.fieldName.trim();
    if (!trimmed) return;

    if (selectedFieldIndex !== null) {
      const updated = fieldNames.map((name, idx) =>
        idx === selectedFieldIndex ? trimmed : name
      );
      setFieldNames(updated);
      setSelectedFieldIndex(null);
    } else {
      if (fieldNames.includes(trimmed)) {
        toast.info("This field name is already in the list");
      }
      setFieldNames([...fieldNames, trimmed]);
    }

    setFormValues((prev) => ({ ...prev, fieldName: "" }));
  };

  // Handle enter key press in field name input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addOrUpdateFieldName();
    }
  };

  // Delete field name from list
  const deleteFieldName = (indexToDelete) => {
    setFieldNames(fieldNames.filter((_, idx) => idx !== indexToDelete));
    if (selectedFieldIndex === indexToDelete) {
      setFormValues((prev) => ({ ...prev, fieldName: "" }));
      setSelectedFieldIndex(null);
    }
  };

  // Click on a field in list to edit it
  const handleFieldClick = (index) => {
    setFormValues((prev) => ({ ...prev, fieldName: fieldNames[index] }));
    setSelectedFieldIndex(index);
  };

  // Submit Table Form
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValues.tableName.trim()) {
      toast.warn("Table Name is required");
      return;
    }
    if (fieldNames.length === 0) {
      toast.warn("Please add at least one field to the table");
      return;
    }

    dispatch(
      addTableWithFields({
        tableName: formValues.tableName.trim(),
        fieldNames,
      })
    );

    // Reset form
    setFormValues({ tableName: "", fieldName: "" });
    setFieldNames([]);
    setSelectedFieldIndex(null);
  };

  // Main table columns for DynamicTable
  const tableColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "tableName",
      headerName: "Table Name",
      flex: 2,
      renderCell: (params) => (
        <span className="font-semibold text-gray-800 flex items-center gap-2">
          <FiDatabase className="text-[#133a69] text-base" />
          {params.row.tableName}
        </span>
      ),
    },
    {
      field: "FildTotal",
      headerName: "Total Fields",
      flex: 1.2,
      renderCell: (params) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold bg-[#E8F2FF] text-[#133a69]">
          {params.row.FildTotal} Fields
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isSelected = selectedRow?.Mid === params.row.Mid;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => handleRowClick(params.row)}
              className={`px-3 py-1 text-xs font-semibold rounded transition ${isSelected
                  ? "bg-[#133a69] text-white"
                  : "bg-gray-200 hover:bg-[#133a69] hover:text-white text-gray-800"
                }`}
            >
              {isSelected ? "Active" : "Inspect"}
            </button>
          </div>
        );
      },
    },
  ];

  // Fields columns for DynamicTable
  const fieldColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "FildName",
      headerName: "Field Name",
      flex: 2,
      renderCell: (params) => (
        <span className="font-medium text-gray-800 flex items-center gap-2">
          <FiFileText className="text-[#133a69]" />
          {params.row.FildName}
        </span>
      ),
    },
    {
      field: "date",
      headerName: "Created Date",
      flex: 1,
      renderCell: (params) => (
        <span className="text-xs text-gray-500">
          {params.row.date}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Top Workflow Navigation */}
      <TableNavTabs title="Table Master" />

      {/* Top Section: Tables and Fields */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left: All Tables DynamicTable */}
        <div
          className={`${selectedRow ? "col-span-12 lg:col-span-6" : "col-span-12"
            } Form_bg_color bg-white shadow-md rounded-md p-4 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              All Tables
            </h3>
            <span className="bg-[#112A47] text-white px-4 py-2 text-sm font-bold rounded shadow">
              Total: {tableRows.length}
            </span>
          </div>

          {tablesLoading ? (
            <div className="p-8 flex justify-center">
              <Loader />
            </div>
          ) : (
            <DynamicTable
              columns={tableColumns}
              rows={tableRows}
              height={380}
              searchable={true}
              onRowClick={handleRowClick}
              getRowClassName={(row) =>
                row.Mid === selectedRow?.Mid ? "!bg-blue-100 font-bold" : ""
              }
            />
          )}
        </div>

        {/* Right: Fields of Selected Table DynamicTable */}
        {selectedRow ? (
          <div className="col-span-12 lg:col-span-6 Form_bg_color bg-white shadow-md rounded-md p-4 animate__animated animate__fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded truncate max-w-[80%]">
                Fields in: {selectedRow.tableName}
              </h3>
              <div className="flex items-center gap-2">
                <span className="bg-[#112A47] text-white px-3 py-1.5 text-xs font-bold rounded shadow">
                  Total: {fieldRows.length}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedRow(null)}
                  className="bg-gray-400 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-gray-500 transition shadow"
                >
                  Close
                </button>
              </div>
            </div>

            {selectedTableLoading ? (
              <div className="p-8 flex justify-center">
                <Loader />
              </div>
            ) : fieldRows.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-gray-400 p-8 text-center bg-[#F3F8FB] border border-dashed border-gray-300 rounded">
                <FiFileText size={40} className="mb-2 text-gray-300" />
                <p className="text-sm">No fields registered for this table.</p>
              </div>
            ) : (
              <DynamicTable
                columns={fieldColumns}
                rows={fieldRows}
                height={380}
                searchable={true}
              />
            )}
          </div>
        ) : null}
      </div>

      {/* Bottom Section: Table Form (Create Table with Fields) */}
      <div className="Form_bg_color bg-white shadow-md rounded-md p-4 mt-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            Table Form (Create New Table)
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
            {/* Table Name Input */}
            <div className="col-span-12 md:col-span-6">
              <label className="font-semibold block mb-1 text-sm text-gray-700">
                Table Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                name="tableName"
                placeholder="Enter Table Name (e.g. PartyMaster, InvoiceDetails)"
                value={formValues.tableName}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded text-sm outline-none focus:border-[#133a69]"
              />
              <p className="text-xs text-gray-500 mt-1">
                Unique identifier for the MongoDB collection / database table.
              </p>
            </div>

            {/* Field Name Input */}
            <div className="col-span-12 md:col-span-6">
              <label className="font-semibold block mb-1 text-sm text-gray-700">
                {selectedFieldIndex !== null ? "Edit Field Name" : "Add Field Name"}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter Field Name (e.g. GSTIN, MobileNo)"
                  name="fieldName"
                  value={formValues.fieldName}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded text-sm outline-none focus:border-[#133a69]"
                />
                <button
                  type="button"
                  onClick={addOrUpdateFieldName}
                  disabled={!formValues.fieldName.trim()}
                  className="All_button text-white px-5 py-2 rounded font-medium text-sm flex items-center gap-1 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow"
                >
                  {selectedFieldIndex !== null ? (
                    <>
                      <MdCheckCircle size={16} /> Update
                    </>
                  ) : (
                    <>
                      <MdAdd size={16} /> Add Field
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Type the column/field name and hit Enter or click "+ Add Field".
              </p>
            </div>

            {/* Field Names Preview */}
            <div className="col-span-12 mt-2">
              <label className="font-semibold block mb-2 text-sm text-gray-700">
                Fields to create ({fieldNames.length}):
              </label>

              {fieldNames.length === 0 ? (
                <div className="p-4 bg-[#F3F8FB] border border-dashed border-gray-400 rounded text-center text-xs text-gray-500">
                  No fields added yet. Add fields using the input above.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 p-3 bg-[#F3F8FB] border-2 border-black/20 rounded mb-4">
                  {fieldNames.map((name, index) => (
                    <span
                      key={index}
                      onClick={() => handleFieldClick(index)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold cursor-pointer transition shadow-sm ${selectedFieldIndex === index
                          ? "bg-[#133a69] text-white"
                          : "bg-white text-gray-800 border border-gray-300 hover:border-black"
                        }`}
                    >
                      <FiFileText size={12} />
                      {name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteFieldName(index);
                        }}
                        className="ml-1 text-red-500 hover:text-red-700"
                        title="Remove field"
                      >
                        <MdDelete size={15} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="col-span-12 pt-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-xs text-gray-600 font-medium">
                Ready to create table with {fieldNames.length} custom field(s).
              </span>
              <button
                type="submit"
                disabled={actionLoading || !formValues.tableName.trim() || fieldNames.length === 0}
                className="All_button text-white px-8 py-2 rounded font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed shadow"
              >
                {actionLoading ? "Submitting..." : "SUBMIT TABLE"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TableMaster;
